import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "motion/react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { brandAsset } from "@mashriqain/brand";
import { useLenis } from "@mashriqain/ui";
import { nav, site } from "@/content/site";
import styles from "./Header.module.css";

export function Header() {
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const lenis = useLenis();

  useMotionValueEvent(scrollY, "change", (y) => {
    const previous = scrollY.getPrevious() ?? 0;
    // Step aside while reading down, return the moment the reader scrolls up.
    setHidden(y > 320 && y > previous);
    setSolid(y > 24);
  });

  // Close the menu on navigation; this is state derived from the route.
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    lenis?.stop();
    document.documentElement.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      lenis?.start();
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, lenis]);

  return (
    <>
      <motion.header
        className={styles.header}
        data-open={open}
        data-solid={solid}
        animate={{ y: hidden && !open ? "-100%" : "0%" }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className={`container ${styles.inner}`}>
          <Link to="/" className={styles.brand} aria-label={`${site.name}, home`}>
            <img
              src={brandAsset("logo-horizontal-ink.svg")}
              alt=""
              className={`${styles.logo} ${styles.logoInk}`}
              width={979}
              height={210}
            />
            <img
              src={brandAsset("logo-horizontal-ivory.svg")}
              alt=""
              className={`${styles.logo} ${styles.logoIvory}`}
              width={979}
              height={210}
            />
          </Link>

          <nav className={styles.nav} aria-label="Primary">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} className={styles.link}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            className={styles.menuButton}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            <span>{open ? "Close" : "Menu"}</span>
            <span className={styles.burger} aria-hidden="true" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            className={`theme-ink ${styles.sheet}`}
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
          >
            <nav className={styles.sheetNav} aria-label="Mobile">
              {[{ to: "/", label: "Home" }, ...nav].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink to={item.to} end className={`display ${styles.sheetLink}`}>
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              className={`ar ${styles.sheetArabic}`}
              lang="ar"
              dir="rtl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              {site.arabicName}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
